/**
 * Wix Instance Token Utilities
 *
 * Wix passes an instance token to embedded apps via URL parameter.
 * The token is a base64-encoded JSON object containing:
 * - instanceId: Unique ID for this app installation
 * - signDate: When the token was signed
 * - uid: Wix user ID (if logged in)
 * - permissions: App permissions
 *
 * Note: For security, always verify tokens on the backend.
 * Frontend decoding is only for UX purposes.
 */

export interface WixInstanceData {
  instanceId: string;
  signDate: string;
  uid?: string;
  permissions?: string;
  demoMode?: boolean;
  siteOwnerId?: string;
  siteMemberId?: string;
  expirationDate?: string;
  loginAccountId?: string;
}

/**
 * Convert base64url to standard base64
 * Wix uses base64url encoding which uses - and _ instead of + and /
 */
function base64UrlToBase64(base64url: string): string {
  // Replace base64url characters with standard base64
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  // Add padding if needed
  const padding = base64.length % 4;
  if (padding) {
    base64 += '='.repeat(4 - padding);
  }
  return base64;
}

/**
 * Decode Wix instance token (base64 JSON)
 *
 * The token format is: base64(signature).base64(JSON)
 * We need the second part (payload) for reading data.
 * Note: Wix uses base64url encoding, not standard base64.
 */
export function decodeWixInstance(instanceToken: string): WixInstanceData | null {
  try {
    // Token format: signature.payload (both base64url encoded)
    const parts = instanceToken.split('.');

    if (parts.length !== 2) {
      console.error('Invalid Wix instance token format - expected signature.payload');
      return null;
    }

    // The payload is the SECOND part (not the first!)
    const encodedPayload = parts[1];

    // Convert from base64url to standard base64
    const base64Payload = base64UrlToBase64(encodedPayload);

    // Decode base64
    const jsonString = atob(base64Payload);
    const data = JSON.parse(jsonString);

    return {
      instanceId: data.instanceId || data.instance_id,
      signDate: data.signDate || data.sign_date,
      uid: data.uid,
      permissions: data.permissions,
      demoMode: data.demoMode || data.demo_mode,
      siteOwnerId: data.siteOwnerId || data.site_owner_id,
      siteMemberId: data.siteMemberId || data.site_member_id,
      expirationDate: data.expirationDate || data.expiration_date,
      loginAccountId: data.loginAccountId || data.login_account_id,
    };
  } catch (error) {
    console.error('Failed to decode Wix instance token:', error);
    return null;
  }
}

/**
 * Get instance token from URL parameters
 */
export function getWixInstanceFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('instance');
}

/**
 * Check if running inside Wix iframe
 */
export function isWixEnvironment(): boolean {
  // Check for instance parameter or Wix-specific globals
  return !!getWixInstanceFromUrl() || window.parent !== window;
}

/**
 * Build API headers for Wix authentication
 */
export function getWixAuthHeaders(instanceToken: string): Record<string, string> {
  return {
    'Authorization': `WixInstance ${instanceToken}`,
    'Content-Type': 'application/json',
  };
}
